import { Injectable } from '@angular/core';
import { ModalityType } from '@app/shared/model/shared.model';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import * as WorklistFilterActions from './worklist-filters.actions';
import { StatusType, UrgencyType } from '@app/api-module/control-centre-api-client';
import { DateTime, Interval } from 'luxon';

export interface WorklistFiltersStateModel {
    pendingFilteredModalities: ModalityType[];
    pendingFilteredUrgencies: UrgencyType[];
    pendingFilteredStatuses: StatusType[];

    filteredModalities: ModalityType[];
    filteredUrgencies: UrgencyType[];
    filteredStatuses: StatusType[];

    timeRangeFilter: Interval;
}

@State<WorklistFiltersStateModel>({
    name: 'worklistFilters',
    defaults: {
        pendingFilteredModalities: [],
        pendingFilteredUrgencies: [],
        pendingFilteredStatuses: [],

        filteredModalities: [],
        filteredUrgencies: [],
        filteredStatuses: [],

        timeRangeFilter: WorklistFiltersState.defaultTimeRange
    }
})
@Injectable()
export class WorklistFiltersState {
    public static defaultTimeRange: Interval = Interval.fromDateTimes(
        DateTime.now().minus({ days: 3 }).startOf('day'),
        DateTime.now().endOf('day')
    );

    @Action(WorklistFilterActions.ApplyFilters)
    applyFilters(context: StateContext<WorklistFiltersStateModel>, _action: WorklistFilterActions.ApplyFilters): void {
        const state = context.getState();
        context.patchState({
            filteredModalities: [...state.pendingFilteredModalities],
            filteredUrgencies: [...state.pendingFilteredUrgencies],
            filteredStatuses: [...state.pendingFilteredStatuses]
        });
    }

    @Action(WorklistFilterActions.ClearFilters)
    clearFilters(context: StateContext<WorklistFiltersStateModel>, _action: WorklistFilterActions.ClearFilters): void {
        context.setState({
            pendingFilteredModalities: [],
            pendingFilteredUrgencies: [],
            pendingFilteredStatuses: [],

            filteredModalities: [],
            filteredUrgencies: [],
            filteredStatuses: [],

            timeRangeFilter: WorklistFiltersState.defaultTimeRange
        });
    }

    @Action(WorklistFilterActions.ClearPendingFilters)
    clearPendingFilters(context: StateContext<WorklistFiltersStateModel>, _action: WorklistFilterActions.ClearPendingFilters): void {
        context.patchState({
            pendingFilteredModalities: [],
            pendingFilteredUrgencies: [],
            pendingFilteredStatuses: []
        });
    }

    @Action(WorklistFilterActions.ResetPendingFilters)
    resetPendingFilters(context: StateContext<WorklistFiltersStateModel>, _action: WorklistFilterActions.ResetPendingFilters): void {
        const state = context.getState();
        context.patchState({
            pendingFilteredModalities: [...state.filteredModalities],
            pendingFilteredUrgencies: [...state.filteredUrgencies],
            pendingFilteredStatuses: [...state.filteredStatuses]
        });
    }

    @Action(WorklistFilterActions.AddModality)
    addModality(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.AddModality): void {
        const state = context.getState();
        context.patchState({
            pendingFilteredModalities: [...state.pendingFilteredModalities, action.filteredModality]
        });
    }

    @Action(WorklistFilterActions.RemoveModality)
    removeModality(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.RemoveModality): void {
        const newModalities = [...context.getState().pendingFilteredModalities];
        const index = newModalities.findIndex(value => value === action.filteredModality);
        newModalities.splice(index, 1);
        context.patchState({
            pendingFilteredModalities: newModalities
        });
    }

    @Action(WorklistFilterActions.AddUrgency)
    addUrgency(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.AddUrgency): void {
        const state = context.getState();
        context.patchState({
            pendingFilteredUrgencies: [...state.pendingFilteredUrgencies, action.filteredUrgency]
        });
    }

    @Action(WorklistFilterActions.RemoveUrgency)
    removeUrgency(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.RemoveUrgency): void {
        const newUrgencies = [...context.getState().pendingFilteredUrgencies];
        const index = newUrgencies.findIndex(value => value === action.filteredUrgency);
        newUrgencies.splice(index, 1);
        context.patchState({
            pendingFilteredUrgencies: newUrgencies
        });
    }

    @Action(WorklistFilterActions.AddStatus)
    addStatus(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.AddStatus): void {
        const state = context.getState();
        context.patchState({
            pendingFilteredStatuses: [...state.pendingFilteredStatuses, action.filteredStatus]
        });
    }

    @Action(WorklistFilterActions.RemoveStatus)
    removeStatus(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.RemoveStatus): void {
        const newStatuses = [...context.getState().pendingFilteredStatuses];
        const index = newStatuses.findIndex(value => value === action.filteredStatus);
        newStatuses.splice(index, 1);
        context.patchState({
            pendingFilteredStatuses: newStatuses
        });
    }

    @Action(WorklistFilterActions.UpdateTimeRangeFilter)
    updateTimeRangeFilter(context: StateContext<WorklistFiltersStateModel>, action: WorklistFilterActions.UpdateTimeRangeFilter): void {
        context.patchState({ timeRangeFilter: action.timeRangeFilter });
    }

    @Selector()
    static noPendingFilters(state: WorklistFiltersStateModel): boolean {
        return (
            state.pendingFilteredModalities.length === 0 &&
            state.pendingFilteredUrgencies.length === 0 &&
            state.pendingFilteredStatuses.length === 0
        );
    }

    @Selector()
    static filtersApplied(state: WorklistFiltersStateModel): boolean {
        return (
            state.filteredModalities.length > 0 ||
            state.filteredUrgencies.length > 0 ||
            state.filteredStatuses.length > 0 ||
            !state.timeRangeFilter.equals(WorklistFiltersState.defaultTimeRange)
        );
    }

    @Selector()
    static getFilteredModalities(state: WorklistFiltersStateModel): ModalityType[] {
        return state.filteredModalities;
    }

    @Selector()
    static getFilteredUrgencies(state: WorklistFiltersStateModel): UrgencyType[] {
        return state.filteredUrgencies;
    }

    @Selector()
    static getFilteredStatuses(state: WorklistFiltersStateModel): StatusType[] {
        return state.filteredStatuses;
    }

    @Selector()
    static getTimeRangeFilter(state: WorklistFiltersStateModel): Interval {
        return state.timeRangeFilter;
    }
}
